import { Pagination } from 'semantic-ui-react'
import {
  OffsetPaginatedResponse,
  PrismaOffsetPagination,
} from '@/utils/fetchHelpers'
import {
  TypeColumn,
  LoadingView,
  AdminBaseTable,
  TableRowProps,
} from './AdminBaseTable'
import { useOffsetPaginatedQuery } from './useOffsetPaginatedQuery'

export interface OffsetPaginatedTableProps<T extends object> {
  columns: TypeColumn<T>[]
  fetchData: (
    pagination: PrismaOffsetPagination
  ) => Promise<OffsetPaginatedResponse<T>>
  tableRowPropsConfig?: TableRowProps<T>
  paginationProps?: PrismaOffsetPagination
}

/*
 * Hook for fetching and rendering a paginated table with offset-based pagination.
 *
 * Offset-based pagination is useful for small datasets, as it allows for fetching
 * any page and sorting by any column. However, it can be inefficient for large datasets
 * as it requires skipping a large number of items to get to a specific page.
 */
export const useOffsetPaginatedTable = <T extends { id: number }>({
  columns,
  fetchData,
  tableRowPropsConfig,
  paginationProps = {
    skip: 0,
    take: 20,
  },
}: OffsetPaginatedTableProps<T>) => {
  const {
    response,
    loading,
    error,
    activePage,
    totalPages,
    handlePageChange,
    resetPage,
    updateItem,
  } = useOffsetPaginatedQuery<OffsetPaginatedResponse<T>>({
    fetchData,
    take: paginationProps.take,
  })

  const renderTable = () => {
    if (loading) {
      return <LoadingView />
    }
    return (
      <>
        <Pagination
          activePage={activePage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
        <div style={{ overflow: 'auto', margin: '10px 10px 10px 0px' }}>
          <AdminBaseTable
            columns={columns}
            data={response?.data ?? []}
            tableRowPropsConfig={tableRowPropsConfig}
          />
        </div>
      </>
    )
  }

  return {
    renderTable,
    updateItem,
    fetchError: error,
    resetPaginationAndActivePage: resetPage,
  }
}
